import { cloneDeep, get, isPlainObject, mergeWith, set } from 'lodash'

import { hasKeyDeep, isDeviceUtilityStructure, pickProp, prop, setUtility } from '@/components/builder/utils'
import { UTILITIES } from '@/components/builder/utils/predefined_sizes'

import { createError, DEVICES, ERRORS, TEXT_PROPS } from './protocol'

const ALWAYS_ALLOWED = ['attributes', 'stylePattern', 'customClassName']

const AGENT_DEFAULTS = {
  grid: { grid: { rows: 1, columns: 2 } },
  flex: { flexRows: 2 },
}

const WRITE_UNITS = new Set([
  'attributes',
  'customClassName',
  'stylePattern',
  'transition',
  'animation',
  'transforms',
  'filters',
  'mask',
  'scroll',
  'imageAlign',
  'background.gradient',
  'background.image',
  'background.blendMode',
  'background.origin',
  'background.mixBlendMode',
])
const WRITE_UNIT_PATTERN = /^(border|ring|outline|stroke|shadow|textShadow)\.(width|radius|offset)$/
const LAYOUT_PATH = /^(flex|grid)\./

const normalizePath = name => String(name).replace(/\[/g, '.').replace(/\]/g, '')

const rootOf = path => normalizePath(path).split('.')[0]

const replaceArrays = (_target, source) => Array.isArray(source) ? source : undefined

const mergeValues = (...values) => mergeWith({}, ...values, replaceArrays)

const isColorObject = value => isPlainObject(value) && ['hex', 'rgba', 'rgbaParsed'].some(key => key in value)

const HEX_COLOR = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i

function hexToRgba(hex) {
  const match = HEX_COLOR.exec(String(hex).trim())
  if (!match) {
    return null
  }

  const digits = match[1].length <= 4 ? [...match[1]].map(char => char + char).join('') : match[1]
  const channel = offset => parseInt(digits.slice(offset, offset + 2), 16)
  const alpha = digits.length === 8 ? Math.round(channel(6) / 255 * 100) / 100 : 1

  return { r: channel(0), g: channel(2), b: channel(4), a: alpha }
}

function completeColor(value) {
  if (!isColorObject(value) || typeof value.hex !== 'string' || value.rgbaParsed) {
    return value
  }

  const rgba = hexToRgba(value.hex)
  if (!rgba) {
    return value
  }

  return { ...value, rgba, rgbaParsed: `rgba(${rgba.r},${rgba.g},${rgba.b},${rgba.a})` }
}

function normalizeColors(value) {
  if (Array.isArray(value)) {
    return value.map(normalizeColors)
  }

  if (isColorObject(value)) {
    return completeColor(value)
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, normalizeColors(item)]))
  }

  return value
}

const isWriteUnit = path => WRITE_UNITS.has(path) || WRITE_UNIT_PATTERN.test(path) || path.startsWith('attributes')

let genericCache = null

function genericProps() {
  if (!genericCache) {
    genericCache = Object.values(prop.all)
      .map(factory => {
        try {
          return pickProp(factory().build())
        } catch (_error) {
          return null
        }
      })
      .filter(item => item?.name)
  }

  return genericCache
}

const genericRoots = () => new Set(genericProps().flatMap(item => [item.name, ...item.keys || []]).map(rootOf))

function propPaths(definition) {
  const names = (definition?.props || []).flatMap(item => [item.name, ...item.keys || []])
  return new Set(names.filter(Boolean).map(normalizePath))
}

function structuralRoots(definition, block) {
  const roots = new Set(Object.keys(AGENT_DEFAULTS[block] || {}))
  ;(definition?.variants || []).forEach(variant => Object.keys(variant?.props || {}).forEach(key => roots.add(key)))
  return roots
}

function acceptsGenericProps(definition) {
  const roots = genericRoots()
  return (definition?.props || []).some(item => item?.name && rootOf(item.name) !== 'attributes' && roots.has(rootOf(item.name)))
}

function propRoots(definition, block) {
  const roots = new Set(ALWAYS_ALLOWED)
  propPaths(definition).forEach(path => roots.add(path.split('.')[0]))
  Object.keys(definition?.defaultProps || {}).forEach(key => roots.add(key))
  structuralRoots(definition, block).forEach(key => roots.add(key))

  if (acceptsGenericProps(definition)) {
    genericRoots().forEach(key => roots.add(key))
  }

  return roots
}

export function validateProps(definition, props, block) {
  if (!isPlainObject(props)) {
    throw createError(ERRORS.INVALID_OP, 'props must be an object')
  }

  if (!definition) {
    return
  }

  const roots = propRoots(definition, block)
  const unknown = Object.keys(props)
    .map(key => normalizePath(key).split('.')[0])
    .filter(root => !roots.has(root))

  if (unknown.length) {
    throw createError(ERRORS.UNKNOWN_PROP, `Unknown prop${unknown.length > 1 ? 's' : ''} for "${block}": ${unknown.join(', ')}`, {
      block,
      unknown,
      known: [...roots].sort(),
    })
  }
}

const matchesPath = (item, path) => normalizePath(item.name) === path || (item.keys || []).some(key => normalizePath(key) === path)

export function describePath(definition, path) {
  const candidates = [...definition?.props || [], ...acceptsGenericProps(definition) ? genericProps() : []]

  for (const item of candidates) {
    if (!item?.name) {
      continue
    }

    if (matchesPath(item, path)) {
      return item
    }

    const group = (item.groups || []).find(entry => normalizePath(entry.name) === path)
    if (group) {
      return { ...item, name: group.name, values: group.values, groups: undefined }
    }
  }

  return null
}

const isScalar = value => ['string', 'number', 'boolean'].includes(typeof value)

export function validateValue(definition, path, value, block) {
  const info = describePath(definition, path)

  if (!info?.values || info.multi || info.creatable || !isScalar(value)) {
    return
  }

  if (!info.values.includes(String(value))) {
    throw createError(ERRORS.INVALID_VALUE, `Invalid value for "${path}" on "${block}": ${JSON.stringify(value)}`, {
      block,
      path,
      value,
      values: info.values,
    })
  }
}

export function normalizeValue(path, value) {
  if (path === 'flex.reverse') {
    return value === true || value === 'true' ? 'true' : undefined
  }

  return normalizeColors(value)
}

function normalizeProps(props) {
  const copy = normalizeColors(cloneDeep(props || {}))
  const reverse = get(copy, 'flex.reverse')

  if (reverse !== undefined) {
    set(copy, 'flex.reverse', normalizeValue('flex.reverse', reverse))
  }

  return copy
}

export function expandProps(definition, props, { leaves = true } = {}) {
  const known = propPaths(definition)
  const hasChildren = prefix => [...known].some(path => path.startsWith(`${prefix}.`))
  const entries = []

  const shouldRecurse = (path, value) => {
    if (!isPlainObject(value) || isDeviceUtilityStructure(value) || isColorObject(value) || !Object.keys(value).length) {
      return false
    }

    if (leaves) {
      return !isWriteUnit(path)
    }

    return !known.has(path) && hasChildren(path)
  }

  const visit = (path, value) => {
    if (shouldRecurse(path, value)) {
      Object.keys(value).forEach(key => visit(`${path}.${key}`, value[key]))
      return
    }

    entries.push([path, value])
  }

  Object.keys(props).forEach(key => visit(normalizePath(key), props[key]))
  return entries
}

export function isIgnoreDeviceProp(definition, path, block) {
  if (path.startsWith('attributes')) {
    return true
  }

  if (structuralRoots(definition, block).has(rootOf(path))) {
    return true
  }

  const ignored = (definition?.ignoreDeviceProps || []).map(normalizePath)
  if (ignored.includes(path)) {
    return true
  }

  const info = describePath(definition, path)
  return Boolean(info?.ignoreDevice || info?.own)
}

const mergeValue = (existing, value, merge) => {
  if (merge && isPlainObject(existing) && isPlainObject(value) && !isDeviceUtilityStructure(existing)) {
    return mergeValues(existing, value)
  }

  return value
}

const MOBILE_ONLY_ROOTS = new Set(['hide', 'visibility'])
const WRAPPER_KEYS = new Set(['default', 'desktop', 'mobile', ...UTILITIES.map(item => item.value)])

const hasKey = (value, key) => isPlainObject(value) && Object.prototype.hasOwnProperty.call(value, key)

const isWrapperStructure = value => isPlainObject(value) && Object.keys(value).length > 0 && Object.keys(value).every(key => WRAPPER_KEYS.has(key))

export function writeProp(properties, path, value, { device = 'desktop', state, ignoreDevice = false, merge = true, fallbackDesktop } = {}) {
  if (!DEVICES.includes(device)) {
    throw createError(ERRORS.INVALID_OP, `Unknown device "${device}"`, { devices: DEVICES })
  }

  let rawValue = get(properties, path)

  if (ignoreDevice) {
    set(properties, path, mergeValue(rawValue, value, merge))
    return
  }

  if (isWrapperStructure(value)) {
    const existing = isWrapperStructure(rawValue) ? rawValue : rawValue !== undefined && !hasKey(value, 'default') && !hasKey(value, 'desktop') && !hasKey(value, 'mobile') ? { default: rawValue } : undefined
    set(properties, path, merge && existing ? mergeValues(existing, value) : value)
    return
  }

  if (device === 'mobile' && rawValue === undefined && fallbackDesktop !== undefined) {
    rawValue = fallbackDesktop
  }

  const mobileOnly = device === 'mobile' && rawValue === undefined && !MOBILE_ONLY_ROOTS.has(path.split('.')[0])
  const warning = mobileOnly ? `"${path}" had no desktop value: classes are mobile-first, so this mobile value applies to every screen until a desktop value is set.` : undefined

  const utility = state ? String(state).split('.') : []
  const { mobile, desktop } = isPlainObject(rawValue) ? rawValue : {}
  const newValue = mergeValue(rawValue, value, merge && !utility.length)

  const values = {
    raw: setUtility(rawValue, newValue, utility),
    desktop: { desktop: setUtility(desktop, newValue, utility), mobile },
    mobile: { mobile: setUtility(mobile, newValue, utility), desktop: desktop || (hasKeyDeep(rawValue, 'mobile') ? undefined : rawValue) },
  }

  const data = device === 'desktop' && !mobile ? values.raw : values[device]
  set(properties, path, data === '' ? undefined : data)
  return warning
}

export const isLayoutPath = path => LAYOUT_PATH.test(path)

export function ensureMobileLayoutType(properties, path) {
  const [kind] = path.split('.')
  const current = get(properties, 'layoutType')

  if (current === undefined) {
    set(properties, 'layoutType', { desktop: kind, mobile: kind })
    return
  }

  if (isPlainObject(current)) {
    if (current.mobile === undefined) {
      set(properties, 'layoutType', { ...current, mobile: current.desktop ?? current.default ?? kind })
    }
    return
  }

  set(properties, 'layoutType', { desktop: current, mobile: current })
}

export const escapeContent = value => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/\r?\n/g, '<br>')

export const isTextProp = (block, path) => path === 'content' || TEXT_PROPS[block] === path

function findVariant(definition, variantKey, block) {
  const variants = definition?.variants || []
  const variant = variants.find(item => item.key === variantKey)

  if (!variant) {
    throw createError(ERRORS.UNKNOWN_VARIANT, `Unknown variant "${variantKey}" for "${block}"`, {
      block,
      variants: variants.map(item => item.key),
    })
  }

  return variant
}

export function mergeBlockProps(definition, variantKey, props, block) {
  const defaults = definition?.defaultProps || {}
  const variant = variantKey ? findVariant(definition, variantKey, block) : null
  const properties = mergeValues(AGENT_DEFAULTS[block] || {}, defaults, variant?.props || {}, normalizeProps(props))

  const textProp = TEXT_PROPS[block]
  if (textProp && typeof properties[textProp] === 'string' && typeof props?.[textProp] === 'string') {
    properties[textProp] = escapeContent(props[textProp])
  }

  return properties
}
